import { ApolloClient, NormalizedCacheObject, useApolloClient, useMutation } from '@apollo/client';
import React, { ReactNode, useEffect, useState } from 'react';
import { AUTHENTICATED_STATUS, CREATE_ACCOUNT, LOGIN } from './auth.queries';
import { clearAuthSessionStorage, getAuthToken, setAuthToken } from './session-storage';
import { HttpStatusCode } from '../http-status-codes';

enum AuthStatus {
    UNAUTHENTICATED,
    AUTHENTICATED,
    PENDING,
    ERROR,
}

const AUTH_ERROR_CODES = new Set(['UNAUTHORIZED', 'UNAUTHENTICATED', 'FORBIDDEN']);

const isUnauthorizedError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
        return false;
    }
    const errDetails = error as any;
    const statusCode =
        errDetails?.networkError?.statusCode ??
        errDetails?.networkError?.status ??
        errDetails?.statusCode ??
        errDetails?.status;

    if ([HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden].includes(statusCode)) {
        return true;
    }

    if (
        Array.isArray(errDetails?.graphQLErrors) &&
        errDetails.graphQLErrors.some((graphError: any) =>
            AUTH_ERROR_CODES.has(String(graphError?.extensions?.code ?? '').toUpperCase())
        )
    ) {
        return true;
    }

    const message = String(errDetails?.message ?? '').toLowerCase();
    return message.includes('unauthorized') || message.includes('unauthenticated');
};

interface AuthContextInnerState {
    status: AuthStatus;
    token?: string;
    error?: Record<string, unknown>;
    adminId?: string;
}

interface AuthContextState extends AuthContextInnerState {
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    createAccount: (variables: any) => Promise<void>;
}

const AuthContext = React.createContext<AuthContextState>({
    status: AuthStatus.UNAUTHENTICATED,
    login: async () => false,
    logout: async () => {},
    createAccount: async () => {},
});

export function AuthProvider({
    children,
    apolloClient,
}: {
    children: ReactNode;
    apolloClient: ApolloClient<NormalizedCacheObject>;
}): React.ReactElement | null {
    const [doLogin] = useMutation(LOGIN);
    const [doCreateAccount] = useMutation(CREATE_ACCOUNT);
    const client = useApolloClient();

    const [state, setState] = useState<AuthContextInnerState>({
        status: AuthStatus.PENDING,
        token: getAuthToken(),
    });
    const [ready, setReady] = useState(false);

    useEffect(() => {
        (async () => {
            const t = getAuthToken();
            if (t) {
                try {
                    const { data } = await client.query({ query: AUTHENTICATED_STATUS });
                    const { authenticated, admin } = data;
                    if (authenticated.status) {
                        setState({
                            token: t,
                            status: AuthStatus.AUTHENTICATED,
                            adminId: admin.id,
                        });
                    } else {
                        clearAuthSessionStorage();
                        setState({ status: AuthStatus.UNAUTHENTICATED, token: undefined });
                    }
                } catch {
                    clearAuthSessionStorage();
                    setState({ status: AuthStatus.UNAUTHENTICATED, token: undefined });
                }
            } else {
                // clear the cache once before mounting children
                await apolloClient.clearStore();
                setState({ status: AuthStatus.UNAUTHENTICATED });
            }
            setReady(true);
        })();
    }, [apolloClient, client]);

    const login = async (username: string, password: string): Promise<boolean> => {
        setState({ status: AuthStatus.PENDING });
        try {
            const { data: authResponse = {} as any } = await doLogin({
                variables: { username, password },
            });
            const { login: loginData } = authResponse;
            const { status, token } = loginData || {};
            if (status) {
                clearAuthSessionStorage();
                setAuthToken(token);
                await apolloClient.clearStore();
                setState({ token, status: AuthStatus.AUTHENTICATED });
                window.location.assign('/'); // Hard redirect to login page to ensure no stale data remains
                return true;
            }
            await apolloClient.clearStore();
            setState({ status: AuthStatus.ERROR });
            return false;
        } catch (error) {
            await apolloClient.clearStore();
            if (isUnauthorizedError(error)) {
                setState({ status: AuthStatus.UNAUTHENTICATED, token: undefined });
                return false;
            }
            setState({ status: AuthStatus.ERROR });
            throw error;
        }
    };

    const logout = async (): Promise<void> => {
        clearAuthSessionStorage();
        await apolloClient.clearStore();
        setState({ status: AuthStatus.UNAUTHENTICATED, token: undefined });
        window.location.assign('/login'); // Hard redirect to login page to ensure no stale data remains
    };

    const createAccount = async (variables: any): Promise<void> => {
        setState({ status: AuthStatus.PENDING });
        const { data: authResponse = {} as any } = await doCreateAccount({ variables });
        const { addAdmin: loginData } = authResponse;
        const { status, token } = loginData || {};
        if (status) {
            clearAuthSessionStorage();
            setAuthToken(token);
            await apolloClient.clearStore();
            setState({ token, status: AuthStatus.AUTHENTICATED });
        } else {
            await apolloClient.clearStore();
            setState({ status: AuthStatus.ERROR });
        }
    };

    // Don’t render children until auth bootstrap is done
    if (!ready) {
        return null;
    }

    const contextState: AuthContextState = {
        ...state,
        login,
        logout,
        createAccount,
    };

    return <AuthContext.Provider value={contextState}>{children}</AuthContext.Provider>;
}

export function useAuthState(): AuthContextState & {
    isAuthenticated: boolean;
    isLoading: boolean;
} {
    const state = React.useContext(AuthContext);
    const isAuthenticated = state.status === AuthStatus.AUTHENTICATED;
    const isLoading = state.status === AuthStatus.PENDING;
    return { ...state, isAuthenticated, isLoading };
}
